/**
 * What a rate is for, when nobody has said — FOR DISPLAY ONLY.
 *
 * ---------------------------------------------------------------------------
 * THE PROBLEM
 * ---------------------------------------------------------------------------
 *
 * A lifted legacy card arrives with no `role` on any rate, because the old
 * format has no such concept and there is nothing to copy. Every screen that
 * reads roles then goes blind on exactly the cards that matter most:
 *
 *   - the price grid draws no bands, because a band is `role: 'nightly'`
 *   - the rate list files all twelve rates under "everything else"
 *   - `nightlyOn` finds nothing, so `inferLadder` returns nothing, so the Ladder
 *     tab shows three INVENTED default rungs beside a real card — which is how
 *     the park found this: "why is the ladder so short compared to the amount of
 *     tariffs?"
 *
 * ---------------------------------------------------------------------------
 * WHY THE OBVIOUS FIX IS WRONG
 * ---------------------------------------------------------------------------
 *
 * The obvious fix is to write the roles onto the card — at lift time, or as a
 * backfill. That was built, and it was wrong, and the thing that caught it was
 * the backfill's own before-and-after price check: **26 of 28 ported cards
 * changed price.**
 *
 * `role: 'nightly'` is not editorial, whatever the comment on `RateRole` used to
 * say. It opts a rate into `flattenNightly`, where overlapping bands are
 * resolved so the NARROWEST window wins and the rest are cut away. That is
 * deliberate and right for a card authored in this system. It is wrong for a
 * lifted one: the legacy format layers several day tariffs over the same nights
 * as competing options and lets the cheapest tiling choose between them, and
 * that behaviour is what 115,385 differential cases actually verify.
 *
 * So the role of a lifted rate cannot be inferred into the data. It can only be
 * inferred for the reader.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS IS
 * ---------------------------------------------------------------------------
 *
 * A pure function from a rate to how a SCREEN should file it. Never stored,
 * never passed to the compiler, and never used to decide a price. Any caller
 * that writes the result to a card is reintroducing the bug above.
 *
 *   one night                  nightly      what a night costs
 *   several nights, repeats    ladder       a week, a fortnight, three weeks
 *   several nights, fixed      arrangement  Easter, the May week, a package
 *   blocks the dates           other        not an offer at all
 */

import type { Programme, Rate, RateRole } from './authoring';

/** The role a rate declares, or the one a screen should assume. */
export function displayRole(rate: Rate): RateRole {
  if (rate.role) return rate.role;
  // Not an offer: it exists to shut the dates, and filing it as a package would
  // put "Closed for works" in a list of things a guest can buy.
  if (rate.availability === 'closed') return 'other';
  if (rate.nights <= 1) return 'nightly';
  return rate.extend ? 'ladder' : 'arrangement';
}

/**
 * True when the card says nothing about roles at all.
 *
 * The distinction matters: a card with SOME roles has been authored here and its
 * silences are meaningful, so guessing over them would be presumptuous. A card
 * with none has simply never been asked, and that is where inference belongs.
 */
export function rolesUnstated(programme: Programme): boolean {
  return programme.rates.length > 0 && programme.rates.every((r) => !r.role);
}

/**
 * The rates a screen should treat as being of a given role.
 *
 * Falls back to inference only for a card that states no roles anywhere.
 */
export function ratesOfRole(programme: Programme, role: RateRole): Rate[] {
  const infer = rolesUnstated(programme);
  return programme.rates.filter((r) => (infer ? displayRole(r) : r.role) === role);
}
